import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import AlertModal from "../components/AlertModal";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [alert, setAlert] = useState({ isOpen: false, message: "", type: "info" });
  const [loaded, setLoaded] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const showAlert = (message, type = "info") => setAlert({ isOpen: true, message, type });
  const closeAlert = () => setAlert({ ...alert, isOpen: false });

  // Fetch wishlist from server, removing any items that no longer exist
  const fetchWishlist = async () => {
    if (!user) return;

    try {
      const response = await axios.get(
        `${BASE_URL}/api/wishlist?email=${encodeURIComponent(user.email)}`,
        { withCredentials: true }
      );

      // filter out any null or invalid items
      const validItems = Array.isArray(response.data)
        ? response.data.filter((item) => item?._id)
        : [];

      setWishlist(validItems);
    } catch (error) {
      console.error("Failed to fetch wishlist:", error);
      setWishlist([]);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Add a product object to wishlist
  const addToWishlist = async (product) => {
    if (!user) {
      showAlert("Please sign in to use the wishlist.", "error");
      setTimeout(() => {
        window.location.href = "/signin";
      }, 3000);
      return;
    }

    if (!product || !product._id) {
      showAlert("Invalid product data", "error");
      return;
    }

    try {
      await axios.post(
        `${BASE_URL}/api/wishlist/add`,
        { email: user.email, product },
        { withCredentials: true }
      );
      fetchWishlist(); // sync from server
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // Remove a product by ID
  const removeFromWishlist = async (productId) => {
    if (!user) return;

    try {
      await axios.post(
        `${BASE_URL}/api/wishlist/remove`,
        { email: user.email, productId },
        { withCredentials: true }
      );

      await fetchWishlist(); // sync with server
    } catch (err) {
      console.error("Failed to remove from wishlist:", err);
    }
  };

  // Clear entire wishlist
  const clearWishlist = async () => {
    if (!user) return;

    try {
      await axios.post(
        `${BASE_URL}/api/wishlist/clear`,
        { email: user.email },
        { withCredentials: true }
      );

      setWishlist([]);
      showAlert("Wishlist cleared", "info");
    } catch (err) {
      console.error("Failed to clear wishlist:", err);
      showAlert("Error clearing wishlist", "error");
    }
  };

  const isInWishlist = (productId) =>
    Boolean(productId) && wishlist.some((item) => item._id === productId);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        addToWishlist,
        removeFromWishlist,
        clearWishlist,
        isInWishlist,
        fetchWishlist,
        loaded,
      }}
    >
      {children}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={closeAlert}
        message={alert.message}
        type={alert.type}
      />
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
